/**
 * File Validation Utilities
 *
 * Client-side validation for file uploads.
 * Validates file type, size, and uses magic bytes to detect spoofed MIME types.
 *
 * Security Features:
 * - MIME type validation
 * - File extension validation
 * - Magic bytes verification (detects renamed files)
 * - File size limits
 */

import { logger } from '@/lib/logger'

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Allowed image MIME types
 */
export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
] as const

/**
 * Allowed image extensions (lowercase)
 */
export const ALLOWED_IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.heic',
  '.heif',
] as const

/**
 * Maximum file size in bytes (10 MB)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024

/**
 * Magic bytes (file signatures) for common image formats
 * Used to detect if a file is truly an image, not a renamed malicious file
 */
const MAGIC_BYTES: Record<string, number[][]> = {
  'image/jpeg': [
    [0xff, 0xd8, 0xff], // JPEG/JFIF
  ],
  'image/png': [
    [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], // PNG
  ],
  'image/gif': [
    [0x47, 0x49, 0x46, 0x38, 0x37, 0x61], // GIF87a
    [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], // GIF89a
  ],
  'image/webp': [
    [0x52, 0x49, 0x46, 0x46], // RIFF (WebP starts with RIFF)
  ],
  // HEIC/HEIF have complex signatures, skip magic byte check for these
}

// ============================================================================
// TYPES
// ============================================================================

export interface FileValidationResult {
  isValid: boolean
  error?: string
  errorCode?:
    | 'INVALID_TYPE'
    | 'INVALID_EXTENSION'
    | 'FILE_TOO_LARGE'
    | 'INVALID_SIGNATURE'
    | 'EMPTY_FILE'
}

export interface FileValidationOptions {
  /** Maximum file size in bytes (default: 10MB) */
  maxSize?: number
  /** Allowed MIME types (default: ALLOWED_IMAGE_TYPES) */
  allowedTypes?: readonly string[]
  /** Whether to check magic bytes (default: true) */
  checkMagicBytes?: boolean
}

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate an image file before upload
 *
 * Checks:
 * 1. File is not empty
 * 2. MIME type is allowed
 * 3. File extension matches MIME type
 * 4. File size is within limits
 * 5. Magic bytes match (optional, for extra security)
 *
 * @param file - The file to validate
 * @param options - Validation options
 * @returns Validation result with isValid and error message
 *
 * @example
 * const result = await validateImageFile(file);
 * if (!result.isValid) {
 *   toast.error(result.error);
 *   return;
 * }
 */
export async function validateImageFile(
  file: File,
  options: FileValidationOptions = {}
): Promise<FileValidationResult> {
  const {
    maxSize = MAX_FILE_SIZE,
    allowedTypes = ALLOWED_IMAGE_TYPES,
    checkMagicBytes = true,
  } = options

  // Check if file exists and has content
  if (!file || file.size === 0) {
    return {
      isValid: false,
      error: 'Please select a file to upload.',
      errorCode: 'EMPTY_FILE',
    }
  }

  // Check file size
  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / 1024 / 1024)
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(1)
    return {
      isValid: false,
      error: `File is too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB.`,
      errorCode: 'FILE_TOO_LARGE',
    }
  }

  // Check MIME type
  const mimeType = file.type.toLowerCase()
  if (
    !allowedTypes.includes(mimeType as (typeof ALLOWED_IMAGE_TYPES)[number])
  ) {
    return {
      isValid: false,
      error: `File type "${mimeType || 'unknown'}" is not supported. Please use JPG, PNG, GIF, or WebP.`,
      errorCode: 'INVALID_TYPE',
    }
  }

  // Check file extension
  const extension = getFileExtension(file.name)
  if (
    !ALLOWED_IMAGE_EXTENSIONS.includes(
      extension as (typeof ALLOWED_IMAGE_EXTENSIONS)[number]
    )
  ) {
    return {
      isValid: false,
      error: `File extension "${extension || 'none'}" is not supported. Please use .jpg, .png, .gif, or .webp.`,
      errorCode: 'INVALID_EXTENSION',
    }
  }

  // Verify magic bytes (optional but recommended for security)
  if (checkMagicBytes) {
    const isMagicBytesValid = await verifyMagicBytes(file, mimeType)
    if (!isMagicBytesValid) {
      logger.warn('File magic bytes do not match MIME type:', {
        fileName: file.name,
        mimeType,
      })
      return {
        isValid: false,
        error:
          'File appears to be corrupted or misnamed. Please try a different file.',
        errorCode: 'INVALID_SIGNATURE',
      }
    }
  }

  return { isValid: true }
}

/**
 * Validate multiple image files
 *
 * @param files - Array of files to validate
 * @param options - Validation options
 * @returns Array of validation results (one per file)
 */
export async function validateImageFiles(
  files: File[],
  options: FileValidationOptions = {}
): Promise<{ file: File; result: FileValidationResult }[]> {
  const results = await Promise.all(
    files.map(async (file) => ({
      file,
      result: await validateImageFile(file, options),
    }))
  )
  return results
}

/**
 * Get all valid files from an array, filtering out invalid ones
 *
 * @param files - Array of files to validate
 * @param options - Validation options
 * @returns Object with valid files and any errors
 */
export async function getValidFiles(
  files: File[],
  options: FileValidationOptions = {}
): Promise<{
  validFiles: File[]
  invalidFiles: { file: File; error: string }[]
}> {
  const results = await validateImageFiles(files, options)

  const validFiles: File[] = []
  const invalidFiles: { file: File; error: string }[] = []

  for (const { file, result } of results) {
    if (result.isValid) {
      validFiles.push(file)
    } else {
      invalidFiles.push({ file, error: result.error || 'Unknown error' })
    }
  }

  return { validFiles, invalidFiles }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get file extension (lowercase, with dot)
 */
function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot === -1) return ''
  return fileName.slice(lastDot).toLowerCase()
}

/**
 * Verify file magic bytes match the expected MIME type
 * This detects renamed files (e.g., malware.exe renamed to image.jpg)
 */
async function verifyMagicBytes(
  file: File,
  mimeType: string
): Promise<boolean> {
  // Skip magic byte check for HEIC/HEIF (complex signatures)
  if (mimeType === 'image/heic' || mimeType === 'image/heif') {
    return true
  }

  const expectedSignatures = MAGIC_BYTES[mimeType]
  if (!expectedSignatures) {
    // No magic bytes defined for this type, skip check
    return true
  }

  try {
    // Read first 16 bytes of file
    const buffer = await file.slice(0, 16).arrayBuffer()
    const bytes = new Uint8Array(buffer)

    // Check if any expected signature matches
    for (const signature of expectedSignatures) {
      if (matchesSignature(bytes, signature)) {
        return true
      }
    }

    // Special case for WebP: Check for RIFF + WEBP
    if (mimeType === 'image/webp') {
      // WebP format: RIFF....WEBP
      const riffMatch = matchesSignature(bytes, [0x52, 0x49, 0x46, 0x46])
      const webpMatch =
        bytes[8] === 0x57 &&
        bytes[9] === 0x45 &&
        bytes[10] === 0x42 &&
        bytes[11] === 0x50
      return riffMatch && webpMatch
    }

    return false
  } catch (error) {
    logger.warn('Failed to read file magic bytes:', error)
    // If we can't read the file, let it through (backend will validate)
    return true
  }
}

/**
 * Check if byte array starts with expected signature
 */
function matchesSignature(bytes: Uint8Array, signature: number[]): boolean {
  if (bytes.length < signature.length) return false
  for (let i = 0; i < signature.length; i++) {
    if (bytes[i] !== signature[i]) return false
  }
  return true
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Sanitize file name (remove special characters)
 */
export function sanitizeFileName(fileName: string): string {
  // Get extension
  const lastDot = fileName.lastIndexOf('.')
  const name = lastDot > 0 ? fileName.slice(0, lastDot) : fileName
  const ext = lastDot > 0 ? fileName.slice(lastDot) : ''

  // Remove/replace dangerous characters
  const sanitized = name
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '') // Remove invalid chars
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/-+/g, '-') // Collapse multiple dashes
    .slice(0, 100) // Limit length

  return sanitized + ext.toLowerCase()
}
